import "@fpsm/cxy-icons/components.css?__remix_sideEffect__";

import { useCallback, useEffect, useRef } from 'react';
import { type Environment } from '@cx-sdk/subs';
import { useLoaderData, useRouteLoaderData } from '@remix-run/react';
import classNames from 'classnames';

import {
  trackLandingFreeTrialClick,
  trackLandingSubscriptionClick,
  trackSubscribeConfirmed } from
'~/components/BettingInsights/tracking';
import {
  Accordion,
  Button,
  List } from
'~/components/OddscheckerPlus/components/cxy';
import {
  type Selection,
  useSubscribe } from
'~/components/Subscribe/hooks/use-subscribe';
import {
  CX_LOGIN_SUCCESS,
  type CXLoginSuccessEvent } from
'~/custom-events/CXLoginSuccessEvent';
import {
  useAuthModal,
  VIEW_LOGIN,
  VIEW_SIGNUP,
  VIEW_VERIFY_PAYMENT } from
'~/providers/auth-modal-provider';
import { type loader as rootLoader } from '~/root-loader';
import { type SubsProduct } from '~/types/subs-product';
import {
  SUBSCRIPTION_TERM,
  type SubscriptionTerm } from
'~/utils/getSubscriptionTerm';

import { PriceCard } from './components/PriceCard';
import { ProductCard } from './components/ProductCard';
import styles from './styles/landing-page.module.css';
import { type loader } from './loader';

export { loader } from './loader';
export { meta } from './meta';

export default function OddscheckerPro() {
  const { close, openAtView } = useAuthModal();
  const { ENV, repub, requestContext, resourcesUrl, user } =
  useRouteLoaderData<typeof rootLoader>('root')!;
  const {
    cancelPayment,
    isLoggedIn,
    isSubscribed,
    monthlyProduct,
    productPrice,
    redirectPath,
    subscriptionTerm,
    verifyPayment,
    yearlyProduct
  } = useLoaderData<typeof loader>();

  const plansRef = useRef<HTMLHeadingElement>(null);

  const { ENVIRONMENT, OC_API_KEY } = ENV;

  const userHasCancelledSubscription = requestContext.user.subscriptions.some(
    ({ status }) => status === 'CANCELLED'
  );

  const onConfirmed = useCallback(() => {
    if (user) {
      trackSubscribeConfirmed({
        product: productPrice,
        term: subscriptionTerm,
        userId: parseInt(user.userId)
      });
    }

    close();
    window.location.href =
    redirectPath ?? `${window.location.href}/positive-ev`;
  }, [close, subscriptionTerm, productPrice, redirectPath, user]);

  const onSubscribe = useCallback(
    (product: SubsProduct, checkout: (product: SubsProduct) => void) => {
      checkout(product);
    },
    []
  );

  const onVerifying = useCallback(() => {
    openAtView(VIEW_VERIFY_PAYMENT);
  }, [openAtView]);

  const { subscribe } = useSubscribe({
    apiKey: OC_API_KEY[repub],
    cancelPayment,
    environment: ENVIRONMENT as Environment,
    initialAuthToken: user?.token || '',
    initialDeviceId: user?.deviceId || '',
    onConfirmed,
    onSubscribe,
    onVerifying,
    redirectPath,
    repub,
    shouldLogin: !isLoggedIn,
    trialDays: 7,
    userSubscriptions: requestContext.user.subscriptions,
    verifyPayment
  });

  const subscribeMontly = useCallback(
    (userData?: CXLoginSuccessEvent['detail']) => {
      subscribe(monthlyProduct, userData);
    },
    [monthlyProduct, subscribe]
  );

  const subscribeYearly = useCallback(
    (userData?: CXLoginSuccessEvent['detail']) => {
      subscribe(yearlyProduct, userData);
    },
    [subscribe, yearlyProduct]
  );

  const trackTrial = (position: 1 | 2 | 3 | 4) => {
    trackLandingFreeTrialClick({ position, userHasCancelledSubscription });
    return true;
  };

  const subscribeTerm = useCallback(
    (term: SubscriptionTerm, userData?: CXLoginSuccessEvent['detail']) => {
      term === SUBSCRIPTION_TERM.MONTHLY ?
      subscribeMontly(userData) :
      subscribeYearly(userData);
    },
    [subscribeMontly, subscribeYearly]
  );

  const selectionRef = useRef<Selection | undefined>();

  const handleProductClick = useCallback(
    (selection: Selection) => {
      selectionRef.current = selection;

      trackLandingSubscriptionClick(selection);

      isLoggedIn ?
      isSubscribed ?
      window.location.reload() :
      subscribeTerm(selection.term) :
      openAtView(VIEW_LOGIN);
    },
    [isLoggedIn, isSubscribed, subscribeTerm, openAtView]
  );

  const onLoginSuccess = useCallback(
    (e: CustomEvent) => {
      if (!selectionRef.current) return;
      subscribeTerm(selectionRef.current.term, e.detail);
    },
    [subscribeTerm]
  );

  useEffect(() => {
    window.addEventListener(CX_LOGIN_SUCCESS, onLoginSuccess);
    return () => {
      window.removeEventListener(CX_LOGIN_SUCCESS, onLoginSuccess);
    };
  }, [onLoginSuccess]);

  const faqs = [
  {
    heading: 'Can I try out oddschecker+ before committing?',
    content: [
    <>
					Absolutely! You can sign up{' '}
					<span className={styles.link} onClick={() => openAtView(VIEW_SIGNUP)}>
						here
					</span>
					{`${userHasCancelledSubscription ? ' again' : ' for a 7-day free trial'}. `}
					You will have full access to our full suite of cutting-edge tools and
					metrics designed to help you find long-term profitability.
				</>]

  },
  {
    heading: 'By using oddschecker+, am I guaranteed to win more?',
    content: [
    <>
					While nothing is a guarantee, our tools leverage our proprietary AI
					algorithms to identify bets with positive expected value, giving you
					an edge over the sportsbooks. Additionally, you can utilize Public
					Betting Splits and trends to spot opportunities with long-term
					returns.
				</>]

  },
  {
    heading: 'Why should I trust this product? ',
    content: [
    <>
					Oddschecker has a 25-plus year history as the global leader in
					providing value for sports bettors. Our state-of-the-art AI models,
					combined with our extensive experience and data, ensure you get the
					best value. Beyond that, the proof is with our users – check out
					social media, App Store reviews, and testimonials to help inform your
					decision.
				</>,
    <>
					{userHasCancelledSubscription ?
      '' :
      "If you're still not convinced, try us out for free for 7 days and see for yourself. "}
					If you have any questions, feel free to reach out to us at{' '}
					<b>assist@oddschecker.com</b> or through our contact form{' '}
					<a
        className={styles.link}
        href="https://oddscheckerus.zendesk.com/hc/en-us/requests/new">

						{' '}
						here{' '}
					</a>{' '}
					. We're on your team. Always.
				</>]

  },
  {
    heading: 'How do I cancel my oddschecker+ subscription?',
    content: [
    <>
					If you are not fully satisfied, you
					{userHasCancelledSubscription ?
      ' cancel your subscription at any point to stop renewals.' :
      ' can cancel your free trial within 7 days at no charge. You can also cancel your subscription at any point to stop renewals.'}
				</>]

  }];


  return (
    <>
			<div className={styles.gradient} />

			<main className={styles.home}>
				<div className={styles.hero} data-testid="hero-content">
					<div>
						<div className={styles.textContainer}>
							<h1 className={styles.title}>
								Bet Smarter With <span>Industry-leading</span> data and ai
							</h1>
							<h2 className={styles.subtitle}>
								25+ years of experience beating the books combined with
								cutting-edge AI geared to help you win.
							</h2>
						</div>
						<Button
              asChild
              className={classNames(styles.tertiaryButton, styles.heroButton)}
              size="large"
              variant="tertiary">

							<a href="/us/plus#plans">Find out more</a>
						</Button>
					</div>
					<picture className={styles.heroImage}>
						<source
              media="(min-width: 801px)"
              srcSet={`${resourcesUrl}/assets/img/plus/hero.png`} />

						<img
              alt="iPhone displaying Positive Value Bets page"
              src={`${resourcesUrl}/assets/img/plus/hero-mobile.png`} />

					</picture>
				</div>
				<ProductCard
          data-testid="product-card-ev"
          image={{
            src: `${resourcesUrl}/assets/img/plus/positive-value-bets.png`,
            alt: 'Image of Positive Value Bets card',
            position: 'leading'
          }}
          title="Positive Value Bets">

					<p data-testid="ev-description">
						Our AI produces 100 million projections each week. We scour over 125
						million price changes each day. Our Positive EV tool gives you our
						best plays where you have a mathematical edge over the books. We've
						thrown in recent trends to show you whether a pick is hot so you've
						got everything you need to beat the odds!
					</p>
					<List
            className={styles.productList}
            data-testid="ev-list"
            items={[
            'Best Odds Available',
            'Current Trends for Each Pick',
            'AI and Implied Probability']
            }
            size="m" />

					<Button
            className={classNames(styles.tertiaryButton, styles.productButton)}
            onClick={() => {
              plansRef.current?.scrollIntoView({
                behavior: 'smooth'
              });
              trackTrial(1);
            }}
            size="large"
            variant="tertiary">

						{userHasCancelledSubscription ? 'Subscribe Now' : 'Try for Free'}
					</Button>
				</ProductCard>
				<ProductCard
          data-testid="product-card-pbd"
          image={{
            src: `${resourcesUrl}/assets/img/plus/public-betting-splits.png`,
            alt: 'Image of Public Betting Splits card',
            position: 'trailing'
          }}
          title="Public Betting Splits">

					<p data-testid="pbd-description">
						Spot the difference between the public and big money sharps! Get the
						most up-to-date betting splits powered by Sportradar, the official
						data provider for the MLB, NBA, and NHL. We monitor 125 million
						price changes each day, so you always catch the best odds. Stay one
						step ahead with oddschecker+!
					</p>
					<List
            className={styles.productList}
            data-testid="pbd-list"
            items={[
            'Public Bet %',
            'Public Money %',
            'Best Odds Across All Sportsbooks']
            }
            size="m" />

					<Button
            className={classNames(styles.tertiaryButton, styles.productButton)}
            onClick={() => {
              plansRef.current?.scrollIntoView();
              trackTrial(2);
            }}
            size="large"
            variant="tertiary">

						{userHasCancelledSubscription ? 'Subscribe Now' : 'Try for Free'}
					</Button>
				</ProductCard>
				<section className={styles.section}>
					<h2 className={styles.sectionTitle} id="plans" ref={plansRef}>
						Plans
					</h2>
					<div className={styles.priceCardWrapper}>
						{[monthlyProduct, yearlyProduct].map((product) =>
            <PriceCard
              data-testid={`${product.term.toLowerCase()}-price`}
              key={product.id}
              list={product.features.map((feature) => feature.name)}
              onClick={() =>
              handleProductClick({
                product: product.price,
                term: product.term
              })
              }
              price={product.price}
              priceFormatted={product.priceFormatted}
              term={product.term}
              title={`${product.term} `}
              {...product.term === SUBSCRIPTION_TERM.ANNUAL && {
                subtitle: 'BEST VALUE: SAVE 33%'
              }}
              userHasCancelledSubscription={userHasCancelledSubscription} />

            )}
					</div>
				</section>
				<section className={styles.section} data-testid="faq-section">
					<h2 className={styles.sectionTitle}>FAQs</h2>
					<p
            className={styles.sectionDescription}
            data-testid="faq-description">

						Discover answers to frequently asked questions about oddschecker+
					</p>
					<Accordion name="faqs" type="multiple" variant="secondary">
						{faqs.map(({ content, heading }, itemIndex) =>
            <Accordion.Item
              data-analytics={`faqs-${itemIndex + 1}`}
              heading={heading}
              key={`accordion-item-${itemIndex}`}
              trackingAttribute={`faqs-${itemIndex + 1}`}>

								{content.map((paragraph, paragraphIndex) =>
              <p
                className={styles.accordionContent}
                key={`accordion-item-${itemIndex}-paragraph-${paragraphIndex}`}>

										{paragraph}
									</p>
              )}
							</Accordion.Item>
            )}
					</Accordion>
				</section>
			</main>
		</>);

}